/**
 * App.tsx — Shield root component.
 *
 * Wraps the entire app in AuthProvider.
 * Public routes: /login, /forgot-password, /reset-password
 * Protected routes: all others (any authenticated user)
 * Admin routes: /admin/* (super_admin only)
 * Sidebar visible only when authenticated; admin section shown to super_admin only.
 */

import React, { useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArticleIcon from '@mui/icons-material/Article';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BusinessIcon from '@mui/icons-material/Business';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import KeyIcon from '@mui/icons-material/Key';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import VerifiedIcon from '@mui/icons-material/Verified';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { AuthProvider, useAuth } from './context/AuthContext';
import { useShieldTheme } from './theme/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';

import TrialBanner from './components/TrialBanner';

import Alerts from './pages/Alerts';
import AutoFix from './pages/AutoFix';
import Dashboard from './pages/Dashboard';
import Scans from './pages/Scans';
import Settings from './pages/Settings';
import ShadowAI from './pages/ShadowAI';
import Tickets from './pages/Tickets';

import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ChangePassword from './pages/auth/ChangePassword';
import Profile from './pages/auth/Profile';
import Signup from './pages/onboarding/Signup';
import Landing from './pages/Landing';
import Contact from './pages/Contact';
import Billing from './pages/Billing';
import NotFound from './pages/NotFound';
import Pricing from './pages/Pricing';
import TrialExpired from './pages/TrialExpired';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';

import NHI from './pages/NHI';
import Compliance from './pages/Compliance';
import ComplianceDetail from './pages/ComplianceDetail';
import CompliancePolicies from './pages/CompliancePolicies';

import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import AccountManagement from './pages/admin/AccountManagement';
import SecurityEvents from './pages/admin/SecurityEvents';
import SystemHealth from './pages/admin/SystemHealth';
import PlanManagement from './pages/admin/PlanManagement';
import TemplateManagement from './pages/admin/TemplateManagement';
import AutoFixCoverage from './pages/admin/AutoFixCoverage';

const DRAWER_WIDTH = 240;

// ── Nav definitions ───────────────────────────────────────────────────────────

const MAIN_NAV = [
  { label: 'Dashboard',  icon: <DashboardIcon />,           path: '/dashboard',  roles: null },
  { label: 'Scans',      icon: <TravelExploreIcon />,        path: '/scans',      roles: null },
  { label: 'Alerts',     icon: <NotificationsActiveIcon />,  path: '/alerts',     roles: null },
  { label: 'Shadow AI',  icon: <VisibilityIcon />,           path: '/shadow-ai',  roles: null },
  { label: 'Auto-Fix',   icon: <AutoFixHighIcon />,          path: '/auto-fix',   roles: null },
  { label: 'Tickets',    icon: <ConfirmationNumberIcon />,   path: '/tickets',    roles: null },
  { label: 'Compliance', icon: <VerifiedIcon />,             path: '/compliance', roles: null },
  { label: 'NHI',        icon: <KeyIcon />,                  path: '/nhi',        roles: null },
  { label: 'Billing',    icon: <CreditCardIcon />,           path: '/billing',    roles: ['customer'] },
  { label: 'Settings',   icon: <AdminPanelSettingsIcon />,   path: '/settings',   roles: ['customer'] },
];

const ADMIN_NAV = [
  { label: 'Admin Dashboard', icon: <AdminPanelSettingsIcon />, path: '/admin' },
  { label: 'Users',           icon: <PeopleIcon />,             path: '/admin/users' },
  { label: 'Accounts',        icon: <BusinessIcon />,           path: '/admin/accounts' },
  { label: 'Security Events', icon: <SecurityIcon />,           path: '/admin/security' },
  { label: 'System Health',   icon: <HealthAndSafetyIcon />,    path: '/admin/health' },
  { label: 'Billing & Plans',      icon: <CreditCardIcon />,         path: '/admin/plans' },
  { label: 'Finding Templates',    icon: <ArticleIcon />,            path: '/admin/templates' },
  { label: 'Auto-Fix Coverage',   icon: <AutoFixHighIcon />,        path: '/admin/autofix-coverage' },
];

function displayRole(role: string): string {
  if (role === 'customer')   return 'Partner';
  if (role === 'super_admin') return 'Super Admin';
  if (role === 'admin')       return 'Admin';
  if (role === 'engineer')    return 'Engineer';
  return role;
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

function Sidebar({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, isEngineer, logout } = useAuth();
  const { tokens } = useShieldTheme();

  function go(path: string) {
    navigate(path);
    onClose?.();
  }

  function isActive(path: string) {
    return location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));
  }

  return (
    <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
        <SecurityIcon sx={{ color: '#00E5FF', fontSize: 28, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
        <Typography variant="h6" noWrap sx={{ color: '#00E5FF', fontWeight: 900, textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
      </Toolbar>
      <Divider />

      {/* Main nav */}
      <List dense>
        {MAIN_NAV.filter(({ roles }) => {
          if (!roles) return true;
          return user && roles.includes(user.role);
        }).map(({ label, icon, path }) => (
          <ListItem key={label} disablePadding>
            <ListItemButton
              onClick={() => go(path)}
              selected={isActive(path)}
              sx={{ borderRadius: 1, mx: 0.5, my: 0.15 }}
            >
              <ListItemIcon sx={{
                color: isActive(path) ? tokens.sidebarTextActive : tokens.sidebarText,
                minWidth: 38,
              }}>
                {icon}
              </ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: isActive(path) ? 600 : 400,
                  color: isActive(path) ? tokens.sidebarTextActive : tokens.sidebarText,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Admin nav — super_admin only */}
      {isAdmin && (
        <>
          <Divider sx={{ mx: 2, my: 1 }} />
          <Typography variant="caption" color="text.disabled" sx={{ px: 2, mb: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>
            Admin
          </Typography>
          <List dense>
            {ADMIN_NAV.map(({ label, icon, path }) => (
              <ListItem key={label} disablePadding>
                <ListItemButton
                  onClick={() => go(path)}
                  selected={isActive(path)}
                  sx={{ borderRadius: 1, mx: 0.5, my: 0.15 }}
                >
                  <ListItemIcon sx={{
                    color: isActive(path) ? 'error.main' : tokens.sidebarText,
                    minWidth: 38,
                  }}>
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: isActive(path) ? 600 : 400,
                      color: isActive(path) ? tokens.sidebarTextActive : tokens.sidebarText,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Engineer nav — Finding Templates visible to engineer (not already shown via isAdmin) */}
      {!isAdmin && isEngineer && (
        <>
          <Divider sx={{ mx: 2, my: 1 }} />
          <Typography variant="caption" color="text.disabled" sx={{ px: 2, mb: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>
            Templates
          </Typography>
          <List dense>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => go('/admin/templates')}
                selected={isActive('/admin/templates')}
                sx={{ borderRadius: 1, mx: 0.5, my: 0.15 }}
              >
                <ListItemIcon sx={{
                  color: isActive('/admin/templates') ? tokens.sidebarTextActive : tokens.sidebarText,
                  minWidth: 38,
                }}>
                  <ArticleIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Finding Templates"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isActive('/admin/templates') ? 600 : 400,
                    color: isActive('/admin/templates') ? tokens.sidebarTextActive : tokens.sidebarText,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}

      <Box sx={{ flex: 1 }} />
      <Divider />

      {/* User footer */}
      {user && (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14, cursor: 'pointer' }}
            onClick={() => go('/profile')}
          >
            {user.full_name[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap fontWeight={500} sx={{ color: tokens.sidebarTextActive }}>{user.full_name}</Typography>
            <Typography variant="caption" noWrap sx={{ textTransform: 'capitalize', color: tokens.sidebarText }}>
              {displayRole(user.role)}
            </Typography>
          </Box>
          <Tooltip title="Logout">
            <IconButton size="small" onClick={logout} sx={{ color: tokens.sidebarText }}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {/* Legal footer */}
      <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1.5, justifyContent: 'center' }}>
        {[['Privacy', '/privacy'], ['Terms', '/terms'], ['Contact', '/contact']].map(([label, path]) => (
          <Typography
            key={label}
            variant="caption"
            onClick={() => go(path)}
            sx={{ cursor: 'pointer', color: tokens.sidebarText, '&:hover': { color: tokens.sidebarTextActive } }}
          >
            {label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

// ── App shell (authenticated layout) ─────────────────────────────────────────

function AppShell({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile top bar */}
      <AppBar
        position="fixed"
        sx={{ display: { sm: 'none' }, zIndex: (t) => t.zIndex.drawer + 1, bgcolor: 'background.paper' }}
        elevation={1}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </IconButton>
          <SecurityIcon sx={{ mr: 1, color: '#00E5FF', filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
          <Typography variant="h6" noWrap sx={{ color: '#00E5FF', fontWeight: 900, textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
          <Box sx={{ flex: 1 }} />
          {user && (
            <>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
                <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: 13 }}>
                  {user.full_name[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => { navigate('/profile'); setAnchorEl(null); }}>
                  <AccountCircleIcon fontSize="small" sx={{ mr: 1 }} /> Profile
                </MenuItem>
                <MenuItem onClick={() => { navigate('/login'); setAnchorEl(null); }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        >
          <Sidebar onClose={() => setMobileOpen(false)} />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
          open
        >
          <Sidebar />
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: { xs: 8, sm: 0 },
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <TrialBanner />
        <Box sx={{ p: 3, flexGrow: 1, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

// ── Impersonation banner ──────────────────────────────────────────────────────

function ImpersonationBanner() {
  const { impersonation, exitImpersonation } = useAuth();
  if (!impersonation) return null;
  return (
    <Box sx={{
      position:        'fixed',
      top:             0,
      left:            0,
      right:           0,
      zIndex:          9999,
      bgcolor:         '#e65100',
      color:           '#fff',
      py:              0.75,
      px:              3,
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'center',
      gap:             2,
      fontWeight:      600,
      fontSize:        14,
      boxShadow:       '0 2px 8px rgba(0,0,0,0.4)',
    }}>
      ⚠ Impersonating {impersonation.targetName} ({impersonation.targetEmail})
      <Box
        component="span"
        onClick={exitImpersonation}
        sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 1, fontWeight: 700 }}
      >
        Exit impersonation →
      </Box>
    </Box>
  );
}

// ── Paused account guard — redirects to /billing except for exempt paths ─────

const PAUSED_EXEMPT = ['/billing', '/profile'];

function PausedGuard({ children }: { children: React.ReactNode }) {
  const { user }   = useAuth();
  const location   = useLocation();
  const isPaused   = user?.account_status === 'paused';
  const isExempt   = PAUSED_EXEMPT.some((p) => location.pathname.startsWith(p));

  if (isPaused && !isExempt) {
    return <Navigate to="/billing?reason=paused" replace />;
  }
  return <>{children}</>;
}

// ── Force password change guard — redirects to /change-password ───────────────

function ForcePasswordChangeGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (user?.force_password_change && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
}

// ── Route tree ────────────────────────────────────────────────────────────────

function AppRoutes() {
  const { user, loading, logoutMsg, clearLogoutMsg } = useAuth();

  if (loading) return null; // AuthContext handles the loading spinner itself

  return (
    <>
    <ImpersonationBanner />
    <Routes>
      {/* Public */}
      <Route path="/login"           element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/signup"          element={user ? <Navigate to="/dashboard" replace /> : <Signup />} />
      <Route path="/onboarding/:sessionId" element={<Signup />} />
      <Route path="/contact"         element={<Contact />} />
      <Route path="/pricing"         element={<Pricing />} />
      <Route path="/privacy"         element={<PrivacyPolicy />} />
      <Route path="/terms"           element={<TermsOfService />} />
      <Route path="/trial-expired"   element={<TrialExpired />} />

      {/* Root: landing page for visitors, dashboard for users */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Landing />} />

      {/* Force-password-change page — needs auth but no shell */}
      <Route path="/change-password" element={
        <ProtectedRoute><ChangePassword /></ProtectedRoute>
      } />

      {/* Authenticated shell */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ForcePasswordChangeGuard>
            <PausedGuard>
            <AppShell>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/scans"     element={<Scans />} />
                <Route path="/alerts"    element={<Alerts />} />
                <Route path="/shadow-ai" element={<ShadowAI />} />
                <Route path="/auto-fix"  element={<AutoFix />} />
                <Route path="/tickets"   element={<Tickets />} />
                <Route path="/profile"   element={<Profile />} />
                <Route path="/billing"               element={<Billing />} />
                <Route path="/settings"              element={<Settings />} />
                <Route path="/nhi"                  element={<NHI />} />
                <Route path="/compliance"           element={<Compliance />} />
                <Route path="/compliance/:framework" element={<ComplianceDetail />} />
                <Route path="/compliance-policies"  element={<CompliancePolicies />} />

                {/* Admin routes */}
                <Route path="/admin" element={
                  <ProtectedRoute minRole="super_admin"><AdminDashboard /></ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute minRole="super_admin"><UserManagement /></ProtectedRoute>
                } />
                <Route path="/admin/accounts" element={
                  <ProtectedRoute minRole="super_admin"><AccountManagement /></ProtectedRoute>
                } />
                <Route path="/admin/security" element={
                  <ProtectedRoute minRole="super_admin"><SecurityEvents /></ProtectedRoute>
                } />
                <Route path="/admin/health" element={
                  <ProtectedRoute minRole="super_admin"><SystemHealth /></ProtectedRoute>
                } />
                <Route path="/admin/plans" element={
                  <ProtectedRoute minRole="super_admin"><PlanManagement /></ProtectedRoute>
                } />
                <Route path="/admin/templates" element={
                  <ProtectedRoute minRole="engineer"><TemplateManagement /></ProtectedRoute>
                } />
                <Route path="/admin/autofix-coverage" element={
                  <ProtectedRoute minRole="super_admin"><AutoFixCoverage /></ProtectedRoute>
                } />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </AppShell>
            </PausedGuard>
            </ForcePasswordChangeGuard>
          </ProtectedRoute>
        }
      />
    </Routes>

    <Snackbar
      open={Boolean(logoutMsg)}
      autoHideDuration={4000}
      onClose={clearLogoutMsg}
      message={logoutMsg}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
    </>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function App() {
  // ShieldThemeProvider (in index.tsx) already owns MUI ThemeProvider + CssBaseline.
  // This component only provides routing and auth context.
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
